import React from "react"
import { useTranslation } from "react-i18next"
import { NavLink, useNavigate } from "react-router-dom"

import PublicLayout from "components/Layout/PublicLayout"
import { useAuth, useOperator } from "hooks"
import { publicRoutes } from "router/routes"

import { AccordionItem } from "features/misc/components/AccordionItem"
import { LeftWave, RightWave, BackIcon } from "components/Elements"

import { Seo } from "components/Seo"

import "./Faq.scoped.scss"
import { BICYCODE_ID } from "operators/bicycode/config"

interface AccordionProps {
	title: string
	description: string
}

export const FaqPage: React.FC = () => {
	const [accordionContent, setAccordionContent] = React.useState<AccordionProps[]>([])
	const [accordionLength, setAccordionLength] = React.useState<number>(1)

	const { isLoggedIn } = useAuth()
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { currentOperator } = useOperator()

	React.useEffect(() => {
		if (currentOperator?.faqAccordionLength)
			setAccordionLength(currentOperator?.faqAccordionLength)

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOperator])

	React.useEffect(() => {
		if (currentOperator?.folderName) {
			const accordion: AccordionProps[] = Array.from(
				{ length: accordionLength },
				(_, i) => i + 1
			).map((itemIndex) => ({
				title: t(`accordion.title_${itemIndex}`, {
					ns: `faq_${currentOperator.folderName}`,
				}),
				description: t(`accordion.text_${itemIndex}`, {
					ns: `faq_${currentOperator.folderName}`,
				}),
			}))

			setAccordionContent(accordion)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentOperator, accordionLength])

	return (
		<PublicLayout>
			{currentOperator && (
				<div className="pt-10 lg:p-10">
					<Seo
						title={t("faq_title", {
							ns: "seo",
							operator: currentOperator.name,
						})}
					/>
					{currentOperator.id === BICYCODE_ID && !isLoggedIn && (
						<>
							<LeftWave className="fixed bottom-0 left-0" />
							<RightWave className="fixed bottom-0 right-0" />
						</>
					)}

					<h1 className={`c-h1 leading-tight mb-6 ${!isLoggedIn ? "text-center" : ""}`}>
						{t("menu.faq")}
					</h1>

					<div className="relative mx-auto pt-10 pl-7 lg:p-10">
						<div className="px-4">
							<div className="max-w-screen-md mx-auto">
								{!isLoggedIn && (
									<button
										aria-label={t("global.back")}
										type="button"
										onClick={() => navigate(-1)}
									>
										<BackIcon size="60" color="var(--primaryColor)" />
									</button>
								)}

								<h2 className="c-h2 text-secondary text-center mb-6">
									{t("title", { ns: `faq_${currentOperator.folderName}` })}
								</h2>
								<div className="mb-16 text-center max-w-screen-sm mx-auto">
									{t("description", {
										ns: `faq_${currentOperator.folderName}`,
									})}
									<NavLink
										to={publicRoutes.contact.path}
										className="align-baseline underline"
									>
										{t("contact-us", {
											ns: `faq_${currentOperator.folderName}`,
										})}
									</NavLink>
									.
								</div>

								{accordionContent.map((item: any, index: number) => (
									<AccordionItem
										title={item.title}
										description={item.description}
										itemNumber={index + 1}
										key={index}
									/>
								))}
							</div>
						</div>
					</div>
				</div>
			)}
		</PublicLayout>
	)
}
